import { locales, pathnames } from "@/i18n/navigation";

/**
 * Routes that live in the `(auth)` group. Their first path segment is derived
 * from the routing config, so renaming a localized slug there is enough.
 */
const LOCALIZED_AUTH_ROUTES = [
  "/sign-in/[[...sign-in]]",
  "/sign-up/[[...sign-up]]",
] as const;

/** Auth routes without a localized alias, matched by their literal segment. */
const PLAIN_AUTH_ROUTES = ["/sign-out", "/sign-with-token"] as const;

const getFirstSegment = (pathname: string) =>
  pathname.split("/").filter(Boolean).at(0);

const buildAuthSegments = () => {
  const segments = new Set<string>();

  for (const route of PLAIN_AUTH_ROUTES) {
    const segment = getFirstSegment(route);
    if (segment) {
      segments.add(segment);
    }
  }

  for (const route of LOCALIZED_AUTH_ROUTES) {
    // The internal route covers locales without an alias.
    const internalSegment = getFirstSegment(route);
    if (internalSegment) {
      segments.add(internalSegment);
    }

    const localized = pathnames[route];
    for (const locale of locales) {
      const segment = getFirstSegment(localized[locale]);
      if (segment) {
        segments.add(segment);
      }
    }
  }

  return segments;
};

const AUTH_SEGMENTS = buildAuthSegments();

const isLocaleSegment = (segment: string) =>
  locales.some((locale) => locale === segment);

/**
 * Whether a browser pathname points at an auth screen. Those screens ship their
 * own minimal header and footer, so the site chrome has to stay out of them.
 */
export const isAuthPathname = (pathname: string) => {
  const [first, second] = pathname.split("/").filter(Boolean);

  if (!first) {
    return false;
  }

  const routeSegment = isLocaleSegment(first) ? second : first;

  return Boolean(routeSegment && AUTH_SEGMENTS.has(routeSegment));
};
